import { Theme } from "@mui/material";
import { CallbackDataParams } from "echarts/types/dist/shared";

import { NO_DATA } from "@/pages/scenario-builder/utils/scenarioConstants";

import { CurveType } from "../constants/maps";
import { ComparisonMode } from "../types/comparisonMode";
import { GraphDataDTO } from "../types/dto";
import { CurveAnalyser } from "./curveAnalysis";
import { getTitle } from "./satCurve";
import { Config, NO_SYMBOL } from "@/hooks/useConfig";
import { FormatCurrencyOptions } from "@/hooks/useConfig/types";

interface FormatTooltipParams {
  theme: Theme;
  params: CallbackDataParams[];
  graphData: GraphDataDTO[];
  curveType: CurveType;
  comparisonMode: ComparisonMode;
  formatCurrency: (value: number, options?: FormatCurrencyOptions) => string;
  config: Config;
}

const getXValue = (param: CallbackDataParams): number | null => {
  if (!param) return null;

  if (param.componentType === "markPoint") {
    return (param.data as { coord: number[] })?.coord?.[0] ?? null;
  }

  if (Array.isArray(param.data)) {
    return (param.data[0] || null) as number | null;
  }

  return null;
};

export const formatTooltip = ({
  theme,
  params,
  graphData,
  curveType,
  comparisonMode,
  formatCurrency,
  config: { revenueLabel, showReturnInsteadOfRevenue },
}: FormatTooltipParams): string => {
  const xValue = params[0] ? getXValue(params[0]) : null;
  const xValueNumber = Number(xValue);

  if (xValue === null || xValue === undefined) return "";

  const plottedSpends: number[] = [];

  const revenueFormatterOptions = showReturnInsteadOfRevenue ? NO_SYMBOL : {};

  const data = graphData
    .map((curve) => {
      const revenueCoordinates = curve.curveTypes[CurveType.REVENUE].curveCoordinates;
      const marginalROICoordinates = curve.curveTypes[CurveType.MARGINAL_ROI].curveCoordinates;
      const yieldCoordinates = curve.curveTypes[CurveType.YIELD].curveCoordinates;

      const revenueAnalyser = new CurveAnalyser(revenueCoordinates);
      const estimatedRevenue = revenueAnalyser.interpolate(xValueNumber);
      const marginalROIAnalyser = new CurveAnalyser(marginalROICoordinates);
      const estimatedMarginalROI = marginalROIAnalyser.interpolate(xValueNumber);
      const yieldAnalyser = new CurveAnalyser(yieldCoordinates);
      const estimatedYield = yieldAnalyser.interpolate(xValueNumber);

      if (!revenueCoordinates) return "";
      const [minX, maxX] = getXAxisRange(revenueCoordinates);

      if (xValueNumber < minX || xValueNumber > maxX) return "";

      const name = getTitle(curve, comparisonMode) || "";
      const spend = xValueNumber;
      const revenue = estimatedRevenue ?? 0;
      const revenueMROI = estimatedMarginalROI ?? 0;
      const revenueYield = estimatedYield ?? 0;

      plottedSpends.push(spend);
      // Determine the content based on curveType
      let tooltipContent = `${revenueLabel}: ${formatCurrency(revenue, revenueFormatterOptions)}<br/>`;

      if (curveType === CurveType.MARGINAL_ROI) {
        tooltipContent += `MROAS: ${revenueMROI?.toFixed(2) ?? NO_DATA}`;
      } else if (curveType === CurveType.YIELD) {
        tooltipContent += `Yield: ${formatCurrency(revenueYield, revenueFormatterOptions) ?? NO_DATA}`;
      }

      const color =
        comparisonMode === ComparisonMode.GEOGRAPHY ? curve.secondaryColor : curve.color;

      return formatTooltipHTML({ name, color, tooltipContent });
    })
    .filter(Boolean)
    .join(`<div style="margin: 8px 0;"></div>`);

  return `<span><strong>Spend:</strong> ${formatCurrency(xValueNumber)}</span>
          <div style="height: 1px; background-color: ${theme.palette.divider}; margin: 8px 0;"></div>
          ${data}`;
};

const getXAxisRange = (coordinates: [number, number][]): [number, number] => {
  const xValues = coordinates.map((coord) => coord[0]);
  return [Math.min(...xValues), Math.max(...xValues)];
};

interface TooltipHTMLParams {
  name: string;
  color: string;
  tooltipContent: string;
}

const formatTooltipHTML = ({ name, color, tooltipContent }: TooltipHTMLParams): string => `
  <div>
    <div style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background-color: ${color}; margin-right: 5px;"></div>
    <strong>${name}</strong>
    <br/>
    <div style="margin-left: 20px;">${tooltipContent}</div>
  </div>
`;
